import { CdkDragEnter, moveItemInArray, PreviewContainer } from '@angular/cdk/drag-drop';
import { AfterViewInit, Component, effect, Input, OnChanges, OnDestroy, SimpleChanges } from '@angular/core';
import { AbstractControl, FormArray, FormControl, FormGroup, ValidatorFn, Validators } from '@angular/forms';
import { escapeSingleQuotes } from '@app/core/functions/escapeSingleQuotes';
import { isEmpty } from '@app/core/functions/isEmpty';
import { isField } from '@app/core/functions/isField';
import { AggregatedDbFieldsType } from '@app/modules/inputs/types/aggregated-db-fields.type';
import { BasicTransformerField } from '@app/modules/query/models/classes/basic-transformer-field.class';
import { QueryStateService } from '@app/modules/query/services/query-state.service';
import { faPlus, IconDefinition } from '@fortawesome/pro-solid-svg-icons';
import { Subject, takeUntil, tap } from 'rxjs';

@Component({
    selector: 'fdx-basic-transformer',
    templateUrl: './basic-transformer.component.html',
    styleUrl: './basic-transformer.component.scss',
    standalone: false
})
export class BasicTransformerComponent implements AfterViewInit, OnChanges, OnDestroy {

    // eslint-disable-next-line @typescript-eslint/typedef
    @Input() transformerForm;
    readonly fieldArrayName: string = 'fieldArray';

    get fieldArray(): FormArray {
        return this.transformerForm.controls[this.fieldArrayName] || new FormArray([]);
    }

    @Input() transformerString: string;

    @Input() disabled?: boolean = false;

    @Input() dbFields: AggregatedDbFieldsType;

    dragging: boolean = false;

    iconPlus: IconDefinition = faPlus;

    cdkDragPreviewContainer: PreviewContainer = 'parent';

    get isDragging(): boolean {
        return this.dragging;
    }

    get isLoading(): boolean {
        return this.queryStateService.isLoading;
    }

    readonly unsubscribe$: Subject<void> = new Subject<void>();

    constructor(
        protected readonly queryStateService: QueryStateService
    ) {
        effect(() => {
            const data = this.queryStateService.queryData();
            if (data?.transformer && !data.error && !data.tooComplex) {
                this.setupForm();
            }
        });
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.disabled && !this.isLoading && this.fieldArray) {
            Object.values(this.fieldArray.controls).forEach((control: AbstractControl) => {
                if (this.disabled) {
                    control.disable();
                } else {
                    control.enable();
                }
            });
        }
    }

    ngAfterViewInit(): void {
        this.transformerForm.valueChanges.pipe(
            tap(() => this.checkAndSetRequiredValidators()),
            takeUntil(this.unsubscribe$))
        .subscribe();
    }

    ngOnDestroy(): void {
        this.unsubscribe$.next();
        this.unsubscribe$.complete();
    }

    setupForm(): void {
        if (!this.transformerForm) {
            this.transformerForm = new FormGroup({});
        }

        const newArray: FormArray = new FormArray([]);
        this.queryStateService.queryData().transformer.forEach((field: BasicTransformerField) => {
            newArray.push(
                this.getFieldGroup(field)
            );
        });

        this.transformerForm.setControl(this.fieldArrayName, newArray);
    }

    checkAndSetRequiredValidators(): void {
        // eslint-disable-next-line @typescript-eslint/typedef
        const controls = Object.values(this.fieldArray.controls);
        const validators: ValidatorFn[] = controls.length > 1 ? [Validators.required] : [];
        controls.forEach((group: FormGroup) => {
            Object.keys(group.value).forEach((fieldName: string) => {
                // eslint-disable-next-line @typescript-eslint/typedef
                const control = group.controls[fieldName];
                control.setValidators(validators);
                control.updateValueAndValidity({emitEvent: false});
            });
        });
    }

    /**
     * This function first simplifies the form by removing any empty rows, then performs
     * some necessary form validation, and finally generates the transformer string. Use this function
     * when you need a final version of the transformer string to be sent to an API.
     * @returns The simplified, finalized transformer string
     */
    finalizeTransformerString(): string {
        if (!this.fieldArray) {
            return '';
        }

        const rowsToRemove: number[] = [];

        // First loop to determine if we can remove any fields
        this.fieldArray.controls.forEach((group: FormGroup, index: number) => {
            if (index > 0 && isEmpty(group.value)) {     // Not first field
                rowsToRemove.push(index);
            }
        });

        rowsToRemove.forEach((row: number) => {
            this.removeFieldClick(row, false);
        });

        this.checkAndSetRequiredValidators();

        // Second loop to actually build the transformer string with the new values
        this.transformerString = this.generateTransformerString();
        return this.transformerString;
    }

    generateTransformerString(): string {
        let newTransformerString: string = '';

        this.fieldArray.controls.forEach((group: FormGroup) => {
            let value: string = Object.values(group.controls)[0].value as string;
            if (value && !isField(value)) {   // If value is not a field
                value = escapeSingleQuotes(value); // re-escape apostraphes
                value = `'${value}'`;                       // add single quotes around string
            } else if (isEmpty(value)) {      // If value is empty
                value = '\'\'';                               // make value empty quotes
            }

            newTransformerString += value;
        });

        return newTransformerString;
    }

    removeFieldClick(index: number, emit: boolean = true): void {
        this.fieldArray.removeAt(index, {emitEvent: emit});
    }

    addNewField(): void {
        this.fieldArray.push(this.getFieldGroup());
    }

    getFieldGroup(field: BasicTransformerField = new BasicTransformerField(null)): FormGroup {
        const value = !isEmpty(field.value) ? field.value.replace(/\\'/g, '\'') : null;

        return new FormGroup({
            field: new FormControl({value: value, disabled: this.disabled})
        });
    }

    controlAsFormGroup(field: AbstractControl): FormGroup {
        return field as FormGroup;
    }

    entered(event: CdkDragEnter): void {
        moveItemInArray(this.fieldArray.controls, event.item.data, event.container.data);
    }
}
